import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Paper, Stack, Checkbox, Chip,
  Alert, Divider, CircularProgress, Tooltip, IconButton,
  Autocomplete, TextField
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import LinkIcon from "@mui/icons-material/Link";
import LockIcon from "@mui/icons-material/Lock";

const API = process.env.REACT_APP_API_URL || "http://localhost:8002";

export default function JoinReview({ connectionId = "oracle_free", onApproved }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState([]);
  const [needsReview, setNeedsReview] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [bridgeGaps, setBridgeGaps] = useState([]);
  const [reviewChecked, setReviewChecked] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");

  // add-join form state
  const [tables, setTables] = useState([]);
  const [leftTable, setLeftTable] = useState("");
  const [leftCol, setLeftCol] = useState("");
  const [rightTable, setRightTable] = useState("");
  const [rightCol, setRightCol] = useState("");
  const [leftCols, setLeftCols] = useState([]);
  const [rightCols, setRightCols] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [userAdded, setUserAdded] = useState([]);

  // load discovered joins
  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch(`${API}/api/oracle/joins/${connectionId}`)
      .then((r) => { if (!r.ok) throw new Error("No discovery found. Run discovery first."); return r.json(); })
      .then((data) => {
        if (!active) return;
        setConfirmed(data.confirmed || []);
        setNeedsReview(data.needs_review || []);
        setSuggestions(data.suggestions || []);
        setBridgeGaps(data.bridge_gaps || []);
        setUserAdded(data.user_added || []);
        setLoading(false);
      })
      .catch((e) => { if (active) { setError(e.message); setLoading(false); } });
    return () => { active = false; };
  }, [connectionId]);

  // load table list for the add-join dropdowns
  useEffect(() => {
    fetch(`${API}/api/oracle/tables`)
      .then((r) => r.json())
      .then((d) => setTables((d.tables || []).map((t) => t.name)))
      .catch(() => {});
  }, []);

  const loadCols = (table, setter) => {
    if (!table) { setter([]); return; }
    fetch(`${API}/api/oracle/columns/${table}`)
      .then((r) => r.json())
      .then((d) => {
        const all = [...(d.measures || []), ...(d.dimensions || [])].map((c) => c.name);
        setter(all);
      })
      .catch(() => setter([]));
  };

  useEffect(() => { loadCols(leftTable, setLeftCols); }, [leftTable]);
  useEffect(() => { loadCols(rightTable, setRightCols); }, [rightTable]);

  const toggleReview = (idx) => setReviewChecked((p) => ({ ...p, [idx]: !p[idx] }));

  // reload everything from the backend (confirmed/needs/suggestions + locked states)
  const reload = () => {
    fetch(`${API}/api/oracle/joins/${connectionId}`)
      .then((r) => r.json())
      .then((data) => {
        setConfirmed(data.confirmed || []);
        setNeedsReview(data.needs_review || []);
        setSuggestions(data.suggestions || []);
        setBridgeGaps(data.bridge_gaps || []);
        setUserAdded(data.user_added || []);
      })
      .catch(() => {});
  };

  // delete a join: persist removal from the approved graph, then reload so a
  // previously-locked suggestion returns to the suggestions list as addable.
  const persistDelete = async (j) => {
    try {
      await fetch(`${API}/api/oracle/joins/${connectionId}/delete-join`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          left_table: j.left_table, left_col: j.left_col,
          right_table: j.right_table, right_col: j.right_col,
        }),
      });
    } catch (e) { /* ignore */ }
    reload();
  };

  const deleteConfirmed = (idx) => {
    const j = confirmed[idx];
    setConfirmed((prev) => prev.filter((_, i) => i !== idx));
    if (j) persistDelete(j);
  };
  const deleteUserAdded = (idx) => {
    const j = userAdded[idx];
    setUserAdded((prev) => prev.filter((_, i) => i !== idx));
    if (j) persistDelete(j);
  };

  // verify a proposed join against data
  const verifyJoin = async () => {
    if (!leftTable || !leftCol || !rightTable || !rightCol) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await fetch(`${API}/api/oracle/joins/${connectionId}/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          left_table: leftTable, left_col: leftCol,
          right_table: rightTable, right_col: rightCol,
        }),
      });
      const json = await res.json();
      setVerifyResult(json);
    } catch (e) {
      setVerifyResult({ success: false, verdict: "error" });
    }
    setVerifying(false);
  };

  const addSuggestion = async (s) => {
    // verify against data, then add (suggestions are pre-verified but we re-check)
    try {
      const res = await fetch(`${API}/api/oracle/joins/${connectionId}/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          left_table: s.left_table, left_col: s.left_col,
          right_table: s.right_table, right_col: s.right_col,
        }),
      });
      const v = await res.json();
      if (v.verdict === "invalid") return;
      setUserAdded((prev) => [...prev, {
        left_table: s.left_table, left_col: s.left_col,
        right_table: s.right_table, right_col: s.right_col,
        ratio: v.ratio, right_type: "dim", source: "user", is_fanout: v.is_fanout,
      }]);
      // remove it from the suggestions list once added
      setSuggestions((prev) => prev.filter((x) =>
        !(x.left_table === s.left_table && x.left_col === s.left_col &&
          x.right_table === s.right_table && x.right_col === s.right_col)));
    } catch (e) { /* ignore */ }
  };

  const addVerifiedJoin = () => {
    if (!verifyResult || verifyResult.verdict === "invalid") return;
    setUserAdded((prev) => [...prev, {
      left_table: leftTable, left_col: leftCol,
      right_table: rightTable, right_col: rightCol,
      ratio: verifyResult.ratio, right_type: "dim",
      source: "user", is_fanout: verifyResult.is_fanout,
    }]);
    // reset form
    setLeftTable(""); setRightTable(""); setLeftCol(""); setRightCol("");
    setVerifyResult(null);
  };

  const buildApprovedSet = () => {
    const approvedReview = needsReview.filter((_, i) => reviewChecked[i]);
    // locked suggestions = already approved earlier; keep them in the active set
    // so they are never dropped on a subsequent approve. (Schema-agnostic.)
    const lockedSuggestions = suggestions.filter((s) => s.locked);
    return [...confirmed, ...approvedReview, ...userAdded, ...lockedSuggestions];
  };

  const save = async (skipped) => {
    setSaving(true);
    setSavedMsg("");
    try {
      const body = skipped
        ? { approved_joins: [], skipped: true }
        : { approved_joins: buildApprovedSet(), skipped: false };
      const res = await fetch(`${API}/api/oracle/joins/${connectionId}/approve`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      setSavedMsg(skipped
        ? `Skipped review — using ${json.approved_count} confirmed joins.`
        : `Saved ${json.approved_count} approved joins.`);
      if (onApproved) onApproved(json);
    } catch (e) {
      setSavedMsg("Save failed: " + e.message);
    }
    setSaving(false);
  };

  const joinLabel = (j) => `${j.left_table}.${j.left_col} = ${j.right_table}.${j.right_col}`;

  if (loading)
    return (<Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={28} />
      <Typography sx={{ mt: 2, color: "#666" }}>Loading discovered joins…</Typography></Box>);
  if (error)
    return (<Box sx={{ p: 3 }}><Alert severity="warning">{error}</Alert></Box>);

  const verdictColor = { valid: "success", fanout: "warning", low_match: "warning", invalid: "error", error: "error" };
  const verdictText = {
    valid: "Valid join — matches data",
    fanout: "Fan-out — would inflate totals",
    low_match: "Low match — partial coverage",
    invalid: "Invalid — no matching rows",
    error: "Verification error",
  };

  return (
    <Box sx={{ bgcolor: "#eceff1", minHeight: "100vh", py: 3 }}>
    <Box sx={{ p: 3, maxWidth: 860, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Review Joins</Typography>
        <Chip size="small" color="primary" variant="outlined" label="Highly recommended" />
      </Stack>
      <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
        Auto-detected joins are verified and active. Delete any that are wrong, add ones we missed,
        or skip to use only the confirmed joins.
      </Typography>

      {/* SUMMARY BAR — how many joins are active vs locked */}
      {(() => {
        const lockedCount = suggestions.filter((s) => s.locked).length;
        const activeTotal = confirmed.length + userAdded.length + lockedCount +
          needsReview.filter((_, i) => reviewChecked[i]).length;
        return (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: "#e3f2fd", borderColor: "#90caf9" }}>
            <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <LinkIcon fontSize="small" sx={{ color: "#1565c0" }} />
                <Typography sx={{ fontWeight: 700, color: "#1565c0" }}>
                  {activeTotal} joins active
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: "#2e7d32" }}>
                {confirmed.length} confirmed
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <LockIcon fontSize="inherit" sx={{ color: "#5e35b1" }} />
                <Typography variant="body2" sx={{ color: "#5e35b1" }}>
                  {lockedCount} locked (approved)
                </Typography>
              </Stack>
              {userAdded.length > 0 && (
                <Typography variant="body2" sx={{ color: "#1565c0" }}>
                  {userAdded.length} you added
                </Typography>
              )}
              {needsReview.length > 0 && (
                <Typography variant="body2" sx={{ color: "#f57f17" }}>
                  {needsReview.length} awaiting review
                </Typography>
              )}
            </Stack>
          </Paper>
        );
      })()}

      {/* CONFIRMED */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "#c8e6c9" }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <CheckCircleIcon sx={{ color: "#2e7d32" }} fontSize="small" />
          <Typography sx={{ fontWeight: 600, color: "#2e7d32" }}>
            Confirmed joins ({confirmed.length}) — active
          </Typography>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        {confirmed.map((j, i) => (
          <Stack key={i} direction="row" alignItems="center" spacing={1}
            sx={{ py: 0.5, px: 1, borderRadius: 1,
                  bgcolor: i % 2 === 0 ? "#f5f5f5" : "#ffffff" }}>
            <CheckCircleIcon sx={{ color: "#66bb6a" }} fontSize="inherit" />
            <Typography variant="body2" sx={{ fontFamily: "monospace", flex: 1 }}>{joinLabel(j)}</Typography>
            <Chip size="small" label={`${Math.round((j.ratio || 0) * 100)}%`} sx={{ height: 18 }} />
            <Tooltip title="Delete this join">
              <IconButton size="small" onClick={() => deleteConfirmed(i)}>
                <DeleteIcon fontSize="small" sx={{ color: "#c62828" }} />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Paper>

      {/* NEEDS REVIEW */}
      {needsReview.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "#ffe082" }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <WarningAmberIcon sx={{ color: "#f57f17" }} fontSize="small" />
            <Typography sx={{ fontWeight: 600, color: "#f57f17" }}>
              Needs your review ({needsReview.length}) — off by default
            </Typography>
          </Stack>
          <Divider sx={{ mb: 1 }} />
          {needsReview.map((j, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1}
              sx={{ px: 1, borderRadius: 1, bgcolor: i % 2 === 0 ? "#fffaf0" : "#ffffff" }}>
              <Checkbox size="small" checked={!!reviewChecked[i]} onChange={() => toggleReview(i)} />
              <Typography variant="body2" sx={{ fontFamily: "monospace", flex: 1 }}>{joinLabel(j)}</Typography>
              {j.is_fanout && (
                <Tooltip title="Produces more rows than the fact table — risks inflating totals">
                  <Chip size="small" color="error" label="fan-out" sx={{ height: 18 }} />
                </Tooltip>
              )}
              <Chip size="small" label={`${Math.round((j.ratio || 0) * 100)}%`} sx={{ height: 18 }} />
            </Stack>
          ))}
        </Paper>
      )}

      {/* USER ADDED */}
      {userAdded.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "#90caf9" }}>
          <Typography sx={{ fontWeight: 600, color: "#1565c0", mb: 1 }}>
            Your added joins ({userAdded.length})
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {userAdded.map((j, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1}
              sx={{ py: 0.5, px: 1, borderRadius: 1,
                    bgcolor: i % 2 === 0 ? "#f5f5f5" : "#ffffff" }}>
              <Typography variant="body2" sx={{ fontFamily: "monospace", flex: 1 }}>{joinLabel(j)}</Typography>
              <Chip size="small" label={`${Math.round((j.ratio || 0) * 100)}%`} sx={{ height: 18 }} />
              <IconButton size="small" onClick={() => deleteUserAdded(i)}>
                <DeleteIcon fontSize="small" sx={{ color: "#c62828" }} />
              </IconButton>
            </Stack>
          ))}
        </Paper>
      )}

      {/* BRIDGE GAP BANNER */}
      {bridgeGaps.length > 0 && (
        <Alert severity="info" icon={<LinkIcon />} sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
            {bridgeGaps.length} connection{bridgeGaps.length > 1 ? "s" : ""} needed for full reporting
          </Typography>
          {bridgeGaps.map((b, i) => (
            <Typography key={i} variant="body2" sx={{ fontFamily: "monospace" }}>
              {b.message}
            </Typography>
          ))}
        </Alert>
      )}

      {/* SUGGESTIONS PANEL */}
      {suggestions.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "#b39ddb", bgcolor: "#faf7ff" }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <LightbulbIcon sx={{ color: "#7e57c2" }} fontSize="small" />
            <Typography sx={{ fontWeight: 600, color: "#5e35b1" }}>
              Suggested joins ({suggestions.length})
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: "#777", display: "block", mb: 1 }}>
            Your data shows these tables connect at a high match rate, but the column names differ.
            We verified them against your data — add the ones you know are correct.
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {suggestions.map((s, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1}
              sx={{ py: 0.5, px: 1, borderRadius: 1,
                    opacity: s.locked ? 0.55 : 1,
                    bgcolor: s.locked ? "#eeeeee" : (i % 2 === 0 ? "#f3eefc" : "#ffffff") }}>
              {s.locked && <LockIcon fontSize="inherit" sx={{ color: "#9e9e9e" }} />}
              <Typography variant="body2" sx={{ fontFamily: "monospace", flex: 1,
                          textDecoration: s.locked ? "none" : "none",
                          color: s.locked ? "#777" : "inherit" }}>
                {s.left_table}.{s.left_col} = {s.right_table}.{s.right_col}
              </Typography>
              <Chip size="small" color={s.locked ? "default" : "success"}
                label={s.locked ? "approved" : `${Math.round((s.ratio || 0) * 100)}% match`}
                sx={{ height: 18 }} />
              {s.locked ? (
                <Chip size="small" variant="outlined" label="locked" sx={{ height: 18 }} />
              ) : (
                <Button size="small" variant="outlined" startIcon={<AddIcon />}
                  onClick={() => addSuggestion(s)}>Add</Button>
              )}
            </Stack>
          ))}
        </Paper>
      )}

      {/* ADD JOIN FORM */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <AddIcon fontSize="small" sx={{ color: "#1565c0" }} />
          <Typography sx={{ fontWeight: 600 }}>Add a join</Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap", gap: 1 }} alignItems="center">
          <Autocomplete freeSolo size="small" options={tables} sx={{ minWidth: 160 }}
            value={leftTable} inputValue={leftTable}
            onInputChange={(e, v) => setLeftTable((v || "").toUpperCase())}
            renderInput={(p) => <TextField {...p} label="Left table" />} />
          <Autocomplete freeSolo size="small" options={leftCols} sx={{ minWidth: 140 }}
            value={leftCol} inputValue={leftCol}
            onInputChange={(e, v) => setLeftCol((v || "").toUpperCase())}
            renderInput={(p) => <TextField {...p} label="Left col" />} />
          <Typography sx={{ alignSelf: "center" }}>=</Typography>
          <Autocomplete freeSolo size="small" options={tables} sx={{ minWidth: 160 }}
            value={rightTable} inputValue={rightTable}
            onInputChange={(e, v) => setRightTable((v || "").toUpperCase())}
            renderInput={(p) => <TextField {...p} label="Right table" />} />
          <Autocomplete freeSolo size="small" options={rightCols} sx={{ minWidth: 140 }}
            value={rightCol} inputValue={rightCol}
            onInputChange={(e, v) => setRightCol((v || "").toUpperCase())}
            renderInput={(p) => <TextField {...p} label="Right col" />} />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" variant="outlined" disabled={verifying || !leftCol || !rightCol}
            onClick={verifyJoin}>
            {verifying ? "Verifying…" : "Verify against data"}
          </Button>
          {verifyResult && (
            <Chip size="small" color={verdictColor[verifyResult.verdict] || "default"}
              label={`${verdictText[verifyResult.verdict] || verifyResult.verdict} (${Math.round((verifyResult.ratio || 0) * 100)}%)`} />
          )}
          {verifyResult && verifyResult.verdict !== "invalid" && verifyResult.verdict !== "error" && (
            <Button size="small" variant="contained" onClick={addVerifiedJoin}>Add this join</Button>
          )}
        </Stack>
      </Paper>

      {savedMsg && <Alert severity="success" sx={{ mb: 2 }}>{savedMsg}</Alert>}

      <Stack direction="row" spacing={2}>
        <Button variant="contained" disabled={saving} onClick={() => save(false)}>
          {saving ? "Saving…" : "Approve all active joins"}
        </Button>
        <Button variant="outlined" disabled={saving} onClick={() => save(true)}>
          Skip — use confirmed only
        </Button>
      </Stack>
    </Box>
    </Box>
  );
}
